import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Chart } from 'primereact/chart';
import { Message } from 'primereact/message';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { HiltiDailyWorkRecord, HiltiProduct, HiltiWorker } from '../types';
import { hiltiDailyWorkAPI, hiltiProductsAPI, hiltiWorkersAPI } from '../services/api';
import { showCustomAlert } from './CustomAlert';

type ChartDataset = {
  labels: string[];
  values: number[];
};

interface WorkerRow {
  workerId: string;
  workerName: string;
  totalsByProduct: Record<string, number>;
  total: number;
}

const HiltiReports: React.FC = () => {
  const [startDate, setStartDate] = useState<Date | null>(() =>
    dayjs().startOf('month').toDate()
  );
  const [endDate, setEndDate] = useState<Date | null>(() => dayjs().endOf('month').toDate());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [workers, setWorkers] = useState<HiltiWorker[]>([]);
  const [products, setProducts] = useState<HiltiProduct[]>([]);
  const [records, setRecords] = useState<HiltiDailyWorkRecord[]>([]);

  const loadRecords = async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setError('');
    try {
      const params = {
        startDate: dayjs(startDate).format('YYYY-MM-DD'),
        endDate: dayjs(endDate).format('YYYY-MM-DD')
      };
      const data = await hiltiDailyWorkAPI.getAll(params);

      // Normalizuj workerId na string (_id) zbog populate na backendu
      const normalized = data.map((rec) => {
        const workerId: any = (rec as any).workerId;
        if (typeof workerId === 'string') {
          return rec;
        }
        if (workerId && typeof workerId === 'object' && '_id' in workerId) {
          return { ...rec, workerId: workerId._id as string };
        }
        return rec;
      });

      setRecords(normalized);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || err?.message || 'Greška pri učitavanju proizvodnje';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadBaseAndRecords = async () => {
      try {
        const [workersRes, productsRes] = await Promise.all([
          hiltiWorkersAPI.getAll({ showInactive: false }),
          hiltiProductsAPI.getAll()
        ]);
        setWorkers(workersRes);
        setProducts(productsRes);
        await loadRecords();
      } catch (err: any) {
        const msg =
          err?.response?.data?.message || err?.message || 'Greška pri učitavanju Hilti podataka';
        setError(msg);
      }
    };
    void loadBaseAndRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const workerRows: WorkerRow[] = useMemo(() => {
    if (!workers.length || !products.length || !records.length) return [];

    const productIds = products.map((p) => p._id);
    const rows: WorkerRow[] = workers.map((w) => ({
      workerId: w._id,
      workerName: `${w.firstName} ${w.lastName}`,
      totalsByProduct: productIds.reduce((acc, pid) => {
        acc[pid] = 0;
        return acc;
      }, {} as Record<string, number>),
      total: 0
    }));

    const rowByWorkerId = rows.reduce<Record<string, WorkerRow>>((acc, row) => {
      acc[row.workerId] = row;
      return acc;
    }, {});

    records.forEach((rec) => {
      const row = rowByWorkerId[rec.workerId];
      if (!row) return;
      rec.products.forEach((p) => {
        const productId = typeof p.productId === 'string' ? p.productId : p.productId._id;
        if (!row.totalsByProduct[productId]) {
          row.totalsByProduct[productId] = 0;
        }
        row.totalsByProduct[productId] += p.quantity || 0;
        row.total += p.quantity || 0;
      });
    });

    return rows.filter((r) => r.total > 0);
  }, [workers, products, records]);

  const productChart: ChartDataset = useMemo(() => {
    const totals = new Map<string, number>();
    records.forEach((rec) => {
      rec.products.forEach((p) => {
        const productId = typeof p.productId === 'string' ? p.productId : p.productId._id;
        totals.set(productId, (totals.get(productId) || 0) + (p.quantity || 0));
      });
    });
    const labels: string[] = [];
    const values: number[] = [];
    products.forEach((p) => {
      const v = totals.get(p._id) || 0;
      if (v > 0) {
        labels.push(p.name);
        values.push(v);
      }
    });
    return { labels, values };
  }, [products, records]);

  const workerChart: ChartDataset = useMemo(() => {
    const totals = new Map<string, number>();
    records.forEach((rec) => {
      const key = rec.workerName || rec.workerId;
      const sum = rec.products.reduce((s, p) => s + (p.quantity || 0), 0);
      totals.set(key, (totals.get(key) || 0) + sum);
    });
    const labels: string[] = [];
    const values: number[] = [];
    Array.from(totals.entries())
      .sort((a, b) => b[1] - a[1])
      .forEach(([name, value]) => {
        if (value > 0) {
          labels.push(name);
          values.push(value);
        }
      });
    return { labels, values };
  }, [records]);

  const chartOptions = useMemo(
    () => ({
      plugins: {
        legend: {
          position: 'bottom' as const
        }
      }
    }),
    []
  );

  const buildPdf = () => {
    if (!startDate || !endDate) {
      showCustomAlert.error('Početni i krajnji datum su obavezni.');
      return;
    }
    if (dayjs(endDate).isBefore(dayjs(startDate), 'day')) {
      showCustomAlert.error('Krajnji datum ne može biti pre početnog.');
      return;
    }

    if (workerRows.length === 0) {
      showCustomAlert.error('Nema podataka za izabrani period.');
      return;
    }

    try {
      const doc = new jsPDF('l', 'pt', 'a4'); // Landscape orientation
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 40;

      // Format datuma
      const formatDate = (date: Date) => {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
      };

      const formatDateTime = (date: Date) => {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}.${month}.${year} ${hours}:${minutes}`;
      };

      // Header
      doc.setFillColor(37, 99, 235); // #2563eb
      doc.rect(0, 0, pageWidth, 80, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text('HILTI IZVEŠTAJ PROIZVODNJE', pageWidth / 2, 30, { align: 'center' });
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      const periodStr = `${formatDate(startDate)} - ${formatDate(endDate)}`;
      doc.text(`Period: ${periodStr}`, pageWidth / 2, 50, { align: 'center' });
      
      doc.setFontSize(9);
      doc.text(`Generisano: ${formatDateTime(new Date())}`, pageWidth / 2, 65, { align: 'center' });

      // Pripremi podatke za tabelu
      const tableHead = ['Radnica', ...products.map(p => p.name), 'Ukupno'];
      const tableBody = workerRows.map((row) => {
        const cells = [
          row.workerName,
          ...products.map(p => {
            const val = row.totalsByProduct[p._id] || 0;
            return val > 0 ? val.toString() : '';
          }),
          row.total.toString()
        ];
        return cells;
      });

      // Tabela
      autoTable(doc, {
        startY: 100,
        head: [tableHead],
        body: tableBody,
        styles: { 
          fontSize: 8,
          cellPadding: 3
        },
        headStyles: { 
          fillColor: [37, 99, 235],
          textColor: [255, 255, 255],
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [249, 250, 251]
        },
        columnStyles: {
          0: { cellWidth: 100 }, // Radnica
          [tableHead.length - 1]: { cellWidth: 60, fontStyle: 'bold' } // Ukupno
        },
        margin: { top: 100, left: margin, right: margin }
      });

      // Footer
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      const totalWorkers = workerRows.length;
      const totalProducts = workerRows.reduce((sum, row) => sum + row.total, 0);
      doc.text(
        `Ukupno radnika sa podacima: ${totalWorkers} | Ukupna proizvodnja: ${totalProducts.toLocaleString('sr-RS')}`,
        pageWidth / 2,
        pageHeight - 20,
        { align: 'center' }
      );

      // Sačuvaj PDF
      const fileName = `hilti-izvestaj-${dayjs(startDate).format('YYYYMMDD')}-${dayjs(endDate).format('YYYYMMDD')}.pdf`;
      doc.save(fileName);
    } catch (error: any) {
      console.error('Error generating PDF:', error);
      showCustomAlert.error('Greška pri generisanju PDF-a');
    }
  };

  const handleGenerate = async () => {
    await loadRecords();
    buildPdf();
  };

  const doughnutColors = [
    '#ec4899',
    '#f97316',
    '#22c55e',
    '#3b82f6',
    '#a855f7',
    '#0ea5e9',
    '#eab308',
    '#6366f1',
    '#14b8a6',
    '#facc15',
    '#64748b',
    '#ef4444'
  ];

  const buildDoughnutData = (dataset: ChartDataset) => ({
    labels: dataset.labels,
    datasets: [
      {
        data: dataset.values,
        backgroundColor: dataset.values.map((_, idx) => doughnutColors[idx % doughnutColors.length]),
        borderWidth: 1
      }
    ]
  });

  return (
    <div className="p-4">
      <h2 className="m-0 mb-4">Hilti Izveštaji</h2>

      {error && (
        <Message severity="error" text={error} className="mb-3" />
      )}

      <Card>
        <div className="grid align-items-end">
          <div className="col-12 md:col-6">
            <label className="block mb-2" style={{ fontWeight: 600 }}>
              Početni datum
            </label>
            <Calendar
              value={startDate}
              onChange={(e) => setStartDate(e.value as Date | null)}
              dateFormat="dd.mm.yy"
              showIcon
              appendTo="self"
              className="w-full"
            />
          </div>
          <div className="col-12 md:col-6">
            <label className="block mb-2" style={{ fontWeight: 600 }}>
              Krajnji datum
            </label>
            <Calendar
              value={endDate}
              onChange={(e) => setEndDate(e.value as Date | null)}
              dateFormat="dd.mm.yy"
              showIcon
              appendTo="self"
              className="w-full"
            />
          </div>
          <div className="col-12 mt-3">
            <Button
              label="Export PDF"
              icon="pi pi-file-pdf"
              className="w-full"
              severity="success"
              onClick={handleGenerate}
              loading={loading}
            />
          </div>
        </div>
      </Card>

      <div className="grid mt-4">
        <div className="col-12 md:col-6 flex justify-content-center">
          <Card
            title="Ukupna proizvodnja po proizvodima"
            style={{ width: '100%', maxWidth: '600px' }}
          >
            {productChart.labels.length ? (
              <Chart
                type="doughnut"
                data={buildDoughnutData(productChart)}
                options={chartOptions}
              />
            ) : (
              <div className="text-600 text-sm">Nema podataka za prikaz.</div>
            )}
          </Card>
        </div>
        <div className="col-12 md:col-6 flex justify-content-center">
          <Card
            title="Ukupna proizvodnja po radnicama"
            style={{ width: '100%', maxWidth: '600px' }}
          >
            {workerChart.labels.length ? (
              <Chart
                type="doughnut"
                data={buildDoughnutData(workerChart)}
                options={chartOptions}
              />
            ) : (
              <div className="text-600 text-sm">Nema podataka za prikaz.</div>
            )}
          </Card>
        </div>
      </div>

      <Card
        title="Detalji proizvodnje po radnicama"
        className="mt-4"
        style={{ fontSize: '0.8rem' }}
      >
        <DataTable
          value={workerRows}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 20, 50]}
          emptyMessage="Nema podataka za izabrani period."
          responsiveLayout="scroll"
          className="text-sm"
        >
          <Column field="workerName" header="Radnica" sortable />
          {products.map((p) => (
            <Column
              key={p._id}
              header={p.name}
              body={(row: WorkerRow) => {
                const val = row.totalsByProduct[p._id] || 0;
                return val ? val.toLocaleString('sr-RS') : '-';
              }}
            />
          ))}
          <Column
            header="Ukupno"
            body={(row: WorkerRow) => row.total.toLocaleString('sr-RS')}
            sortable
          />
        </DataTable>
      </Card>
    </div>
  );
};

export default HiltiReports;


